import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { EnvelopeSimple, LockSimple, Eye, EyeSlash, ArrowRight, CheckCircle } from "@phosphor-icons/react";

/**
 * /forgot-password — user enters email, we call POST /auth/forgot-password
 * which is always 200 (enumeration-resistant). We show a generic success
 * message either way and prompt them to check their inbox.
 */
export function ForgotPassword() {
  const navigate = useNavigate();
  const location = useLocation();
  const preFill = new URLSearchParams(location.search).get("email") || "";
  const [email, setEmail] = useState(preFill);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      setSent(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F6F2] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <button
            onClick={() => navigate("/")}
            className="inline-block text-2xl font-[Manrope] font-bold text-[#2C4C3B] hover:opacity-80"
          >
            MedEasy
          </button>
        </div>

        <div className="bg-white rounded-2xl shadow-[0_4px_24px_rgba(30,36,32,0.06)] border border-[#E3E1D9] p-8">
          {!sent ? (
            <>
              <h1 className="font-[Manrope] font-bold text-2xl text-[#1E2420] leading-tight">
                Reset your password
              </h1>
              <p className="text-sm text-[#5C6660] mt-2 leading-relaxed">
                Enter the email you signed up with. We&apos;ll send you a link to set a new password. The link expires in 60 minutes.
              </p>

              <form onSubmit={submit} className="mt-6 space-y-4" data-testid="forgot-password-form">
                <div className="space-y-1.5">
                  <Label htmlFor="fp-email" className="text-sm font-medium text-[#1E2420]">
                    Email
                  </Label>
                  <div className="relative">
                    <EnvelopeSimple size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C6660]" />
                    <Input
                      id="fp-email"
                      data-testid="forgot-password-email"
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      className="pl-10 h-12 rounded-xl border-[#E3E1D9] focus-visible:ring-[#2C4C3B]"
                      autoFocus
                    />
                  </div>
                </div>

                {error && (
                  <div
                    role="alert"
                    data-testid="forgot-password-error"
                    className="rounded-xl bg-[#C17767]/10 border border-[#C17767]/25 text-[#8C4A3D] px-4 py-3 text-sm"
                  >
                    {error}
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={busy || !email}
                  data-testid="forgot-password-submit"
                  className="w-full h-12 rounded-full bg-[#2C4C3B] hover:bg-[#3A634D] text-white font-semibold"
                >
                  {busy ? "Sending…" : "Email me a reset link"}
                  {!busy && <ArrowRight size={18} className="ml-2" />}
                </Button>
              </form>
            </>
          ) : (
            <div data-testid="forgot-password-sent" className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#2C4C3B]/8 mb-4">
                <CheckCircle size={40} weight="duotone" className="text-[#2C4C3B]" />
              </div>
              <h1 className="font-[Manrope] font-bold text-2xl text-[#1E2420]">
                Check your inbox
              </h1>
              <p className="text-sm text-[#5C6660] mt-3 leading-relaxed">
                If an account exists for <strong className="text-[#1E2420] break-all">{email}</strong>, we&apos;ve sent a password reset link. It expires in 60 minutes and can only be used once.
              </p>
              <p className="text-xs text-[#5C6660] mt-4 leading-relaxed">
                Didn&apos;t see it? Check your spam folder, or wait 60 minutes and try again.
              </p>
            </div>
          )}

          <div className="mt-6 pt-6 border-t border-[#E3E1D9] text-center">
            <button
              onClick={() => navigate("/login")}
              data-testid="forgot-password-back-to-login"
              className="text-sm text-[#5C6660] hover:text-[#2C4C3B] underline underline-offset-4"
            >
              ← Back to sign in
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


/**
 * /reset-password?token=… — user sets a new password using the emailed link.
 * On success we get an auth cookie back and route straight into /log.
 */
export function ResetPassword() {
  const navigate = useNavigate();
  const location = useLocation();
  const { refresh } = useAuth();
  const token = new URLSearchParams(location.search).get("token") || "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [ok, setOk] = useState(false);

  useEffect(() => {
    if (!token) setError("This reset link is missing its token. Please request a new one.");
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setError("The two passwords don't match.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setOk(true);
      toast.success("Password reset — you're now signed in.");
      await refresh();
      setTimeout(() => navigate("/log", { replace: true }), 900);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      setError(typeof msg === "string" ? msg : "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F6F2] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <button
            onClick={() => navigate("/")}
            className="inline-block text-2xl font-[Manrope] font-bold text-[#2C4C3B] hover:opacity-80"
          >
            MedEasy
          </button>
        </div>

        <div className="bg-white rounded-2xl shadow-[0_4px_24px_rgba(30,36,32,0.06)] border border-[#E3E1D9] p-8">
          <h1 className="font-[Manrope] font-bold text-2xl text-[#1E2420] leading-tight">
            Set a new password
          </h1>
          <p className="text-sm text-[#5C6660] mt-2 leading-relaxed">
            Choose a password of at least 8 characters. You&apos;ll be signed in automatically.
          </p>

          {!token ? (
            <div
              role="alert"
              data-testid="reset-password-no-token"
              className="mt-6 rounded-xl bg-[#C17767]/10 border border-[#C17767]/25 text-[#8C4A3D] px-4 py-3 text-sm"
            >
              This reset link is missing its token. Please request a new one from{" "}
              <button
                onClick={() => navigate("/forgot-password")}
                className="underline underline-offset-4 font-semibold"
              >
                Forgot your password
              </button>
              .
            </div>
          ) : (
            <form onSubmit={submit} className="mt-6 space-y-4" data-testid="reset-password-form">
              <div className="space-y-1.5">
                <Label htmlFor="rp-password" className="text-sm font-medium text-[#1E2420]">
                  New password
                </Label>
                <div className="relative">
                  <LockSimple size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C6660]" />
                  <Input
                    id="rp-password"
                    data-testid="reset-password-input"
                    type={showPw ? "text" : "password"}
                    autoComplete="new-password"
                    required
                    minLength={8}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="pl-10 pr-10 h-12 rounded-xl border-[#E3E1D9] focus-visible:ring-[#2C4C3B]"
                    autoFocus
                  />
                  <button
                    type="button"
                    data-testid="reset-password-toggle"
                    onClick={() => setShowPw(!showPw)}
                    aria-label={showPw ? "Hide password" : "Show password"}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5C6660] hover:text-[#1E2420]"
                  >
                    {showPw ? <EyeSlash size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="rp-confirm" className="text-sm font-medium text-[#1E2420]">
                  Confirm new password
                </Label>
                <div className="relative">
                  <LockSimple size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C6660]" />
                  <Input
                    id="rp-confirm"
                    data-testid="reset-password-confirm"
                    type={showPw ? "text" : "password"}
                    autoComplete="new-password"
                    required
                    minLength={8}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    placeholder="••••••••"
                    className="pl-10 h-12 rounded-xl border-[#E3E1D9] focus-visible:ring-[#2C4C3B]"
                  />
                </div>
              </div>

              {error && (
                <div
                  role="alert"
                  data-testid="reset-password-error"
                  className="rounded-xl bg-[#C17767]/10 border border-[#C17767]/25 text-[#8C4A3D] px-4 py-3 text-sm"
                >
                  {error}
                </div>
              )}

              {ok && (
                <div
                  data-testid="reset-password-ok"
                  className="rounded-xl bg-[#2C4C3B]/8 border border-[#2C4C3B]/25 text-[#2C4C3B] px-4 py-3 text-sm inline-flex items-center gap-2"
                >
                  <CheckCircle size={18} weight="duotone" />
                  Password reset — redirecting…
                </div>
              )}

              <Button
                type="submit"
                disabled={busy || !password || !confirm}
                data-testid="reset-password-submit"
                className="w-full h-12 rounded-full bg-[#2C4C3B] hover:bg-[#3A634D] text-white font-semibold"
              >
                {busy ? "Saving…" : "Set new password"}
                {!busy && <ArrowRight size={18} className="ml-2" />}
              </Button>
            </form>
          )}

          <div className="mt-6 pt-6 border-t border-[#E3E1D9] text-center">
            <button
              onClick={() => navigate("/login")}
              data-testid="reset-password-back-to-login"
              className="text-sm text-[#5C6660] hover:text-[#2C4C3B] underline underline-offset-4"
            >
              ← Back to sign in
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
